import React, { useEffect, useState } from 'react';
import { Button, Typography, Box } from '@mui/material';
import suggestedQuestionsMap from '../constants/suggestedQuestions';
import { supabase } from '../supabaseClient';

const SuggestedQuestions = ({ domain, selectedTable, onQuestionSelect, isUploadOpen }) => {
  const [canChat, setCanChat] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email;

      if (!email || !domain) {
        setCanChat(false);
        return;
      }

      // Palmtop is always available to every user, no Supabase permission needed.
      if (domain === 'Palmtop') {
        setCanChat(true);
        return;
      }

      // Keep this in sync with the identical maps in ChatInput.js and NavBar.js.
      // A domain missing here resolves to no agentKey, which sets canChat false
      // and silently hides every suggested question for that agent.
      const domainToAgent = {
        'Sales Order': 'sales',
        'Business Partner': 'sales',
        'Sales Quotation': 'sales',

        'Vendor': 'purchase',
        'Purchase Orders': 'purchase',
        'Purchase Requisition': 'purchase',
        'Goods Movement': 'purchase',
        'Vendor Invoice': 'purchase',

        'Human Resource': 'hr',
        'Policy Repository': 'hr',

        'Cost Center': 'finance',
        'Analytics': 'finance',
        'Finance Analyst': 'finance',
        'Vendor Ledger': 'finance',

        'Inventory': 'inventory',

        'Palmtop': 'palmtop',
      };

      const agentKey = domainToAgent[domain];

      if (!agentKey) {
        setCanChat(false);
        return;
      }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('agents')
        .eq('email', email)
        .single();

      const agents = profile?.agents || {};
      const hasChat = agents?.[agentKey]?.includes('chat');

      setCanChat(Boolean(hasChat));
    };

    loadPermissions();
  }, [domain]);

  // ⛔ NEW: Hide suggested questions when upload panel is open
  if (isUploadOpen) return null;

  const questions =
    suggestedQuestionsMap[domain]?.[selectedTable] || [
      'Which customers are blocked for delivery due to Change in quantity?',
      'Count the number of customers for each OrderBlockCode.',
      'Show me the details of bata company.',
    ];

  return (
    <Box
      className="suggested-questions"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        flex: 1,
        width: '100%',
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 2rem',
      }}
    >
      <Typography
        variant="h5"
        component="p"
        sx={{
          color: 'text.primary',
          marginTop: '16px',
          marginBottom: '12px',
          fontFamily: 'Calibri, sans-serif',
          fontWeight: '500',
          fontSize: '39px',
          textAlign: 'center',
        }}
      >
        Hey I'm Ale-X, How may I help you..
      </Typography>

      {canChat && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: 'Calibri, sans-serif',
              fontSize: '1.5rem',
              marginBottom: '8px',
            }}
          >
            Few questions suggested by Ale-X
          </Typography>

          {questions.map((q, i) => (
            <Button
              key={i}
              variant="contained"
              onClick={() => onQuestionSelect(q)}
              sx={{
                padding: '5px 8px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 500,
                backgroundColor: (t) =>
                  t.palette.mode === 'dark'
                    ? '#1e1e1e'
                    : t.palette.background.paper,
                color: 'text.primary',
                border: (t) => `1px solid ${t.palette.divider}`,
                marginTop: '8px',
                textTransform: 'none',
                maxWidth: 'fit-content',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light' ? '#e0e0e0' : '#444',
                },
              }}
            >
              {q}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SuggestedQuestions;
